import type { AdminPickerRecentReportDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { LinkStateBadge, ReportStatusBadge } from './PickerBadges';
import { tokens } from '../../../design';
import { formatDate } from '../../../utils/format';
import { SectionTitle } from '../../inspectors/_components/InfoBlocks';

/**
 * «Последние отчёты» (AUT-400): the picker's latest checklist reports, the
 * server caps them at 10. The mockup's «Чей» (personal vs company) column
 * waits for the companies epic (CP-10b), so it is not rendered.
 */
const ReportRow = ({ report }: { report: AdminPickerRecentReportDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2" fontWeight={500}>
          {report.carTitle ?? t('pickers.detail.reports.noCar')}
        </Typography>
        <Typography variant="caption" fontFamily="monospace" sx={{ color: tokens.text.tertiary }}>
          {report.reportNumber}
        </Typography>
      </TableCell>
      <TableCell sx={{ fontFamily: 'monospace' }}>{formatDate(report.createdAt)}</TableCell>
      <TableCell>
        <ReportStatusBadge status={report.status} />
      </TableCell>
      <TableCell>
        <LinkStateBadge link={report.link} />
      </TableCell>
    </TableRow>
  );
};

export const PickerRecentReports = ({
  reports,
  reportCount,
}: {
  reports: AdminPickerRecentReportDto[];
  reportCount: number;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box>
      <SectionTitle>{t('pickers.detail.reports.title', { count: reportCount })}</SectionTitle>
      {reports.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('pickers.detail.reports.empty')}
        </Typography>
      ) : (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '46%' }}>{t('pickers.detail.reports.report')}</TableCell>
                <TableCell>{t('pickers.detail.reports.createdAt')}</TableCell>
                <TableCell>{t('pickers.detail.reports.statusLabel')}</TableCell>
                <TableCell>{t('pickers.detail.reports.linkLabel')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((r) => (
                <ReportRow key={r.id} report={r} />
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};
